import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import {
  applyDocumentLanguage,
  applyDomTranslations,
  COMMON_TRANSLATIONS,
  formatLocalizedDate,
  getInitialLanguage,
  getLanguageOption,
  LANGUAGES,
  persistLanguage,
  translateText,
  withLanguageParam,
} from '../../../shared/localization/core'
import type { LanguageCode, LanguageOption, SiteMeta } from '../../../shared/localization/core'

interface LanguageContextValue {
  language: LanguageCode
  languageOption: LanguageOption
  languages: LanguageOption[]
  setLanguage: (language: LanguageCode) => void
  t: (text: string) => string
  apiPath: (path: string) => string
  formatDate: (value: string | undefined) => string
}

const LanguageContext = createContext<LanguageContextValue | null>(null)

export const minecraftMeta: SiteMeta = {
  title: {
    en: 'Uliunai Minecraft',
    lt: 'Uliunai Minecraft',
    ru: 'Uliunai Minecraft',
    fr: 'Uliunai Minecraft',
    de: 'Uliunai Minecraft',
  },
  description: {
    en: 'Minecraft Survival and Modded servers for the Uliunai community.',
    lt: 'Minecraft Survival ir Modded serveriai Uliunai bendruomenei.',
    ru: 'Minecraft Survival и Modded серверы для сообщества Uliunai.',
    fr: 'Serveurs Minecraft Survival et Modded pour la communauté Uliunai.',
    de: 'Minecraft Survival und Modded Server für die Uliunai Community.',
  },
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<LanguageCode>(() => getInitialLanguage())

  const value = useMemo<LanguageContextValue>(() => {
    const setLanguage = (next: LanguageCode) => {
      persistLanguage(next)
      setLanguageState(next)
    }

    return {
      language,
      languageOption: getLanguageOption(language),
      languages: LANGUAGES,
      setLanguage,
      t: (text: string) => translateText(text, language, COMMON_TRANSLATIONS),
      apiPath: (path: string) => withLanguageParam(path, language),
      formatDate: (value: string | undefined) => formatLocalizedDate(value, language),
    }
  }, [language])

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
}

export function useLanguage() {
  const context = useContext(LanguageContext)
  if (!context) throw new Error('useLanguage must be used inside LanguageProvider')
  return context
}

export function LocalizationRuntime({ meta }: { meta: SiteMeta }) {
  const { language } = useLanguage()

  useEffect(() => {
    applyDocumentLanguage(language, meta)

    const root = document.getElementById('root') ?? document.body
    const apply = () => applyDomTranslations(root, language, COMMON_TRANSLATIONS)
    apply()

    let frame: number | null = null
    const observer = new MutationObserver(() => {
      if (frame !== null) return
      frame = window.requestAnimationFrame(() => {
        frame = null
        apply()
      })
    })

    observer.observe(root, { childList: true, subtree: true, characterData: true })

    return () => {
      observer.disconnect()
      if (frame !== null) window.cancelAnimationFrame(frame)
    }
  }, [language, meta])

  return null
}

